import React, { Fragment, useMemo } from 'react';
import styled from 'styled-components';
import { EventConditionInfo } from '../../components';
import {
  FunnelOrderKey,
  IDurationFieldsFragment,
  IFunnelOrder,
  IFunnelQueryFieldsFragment,
  IPartitionValueFieldsFragment,
  Maybe,
} from '../../../../utils/apollo/graphqlSchema';
import * as BuilderInfoUI from '../../BuilderInfoUI';
import { partitionDictionary } from '../../../../redux/slices/oldBuilder/defaults';
import FunnelStepPartitionContent from './FunnelStepPartitionContent';
import {
  ConnectorContainer,
  ConnectorWrapper,
} from '../../../Builder/BuilderUI';
import BadgeRound from '../../../BadgeRound';

interface IProps {
  query: IFunnelQueryFieldsFragment;
}

const StepContainer = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
`;

const EventContainer = styled.div`
  flex: 1;
`;

const StyledStepLabel = styled(BadgeRound)`
  margin-right: 8px;
`;

const AddonContainer = styled.div`
  margin-left: 24px;
`;

const StyledOperator = styled(ConnectorWrapper)`
  padding: 3px 4px;
`;

interface IStepOrderProps {
  stepFunnelOrder: IFunnelOrder;
  globalFunnelOrder: IFunnelOrder;
}

const StepOrder = ({ stepFunnelOrder, globalFunnelOrder }: IStepOrderProps) => {
  const label = useMemo(() => {
    if (globalFunnelOrder.key === FunnelOrderKey.Any) {
      return 'Any order';
    }
    if (globalFunnelOrder.key === FunnelOrderKey.Custom) {
      return stepFunnelOrder.name;
    }
    return globalFunnelOrder.name;
  }, [globalFunnelOrder, stepFunnelOrder]);

  return (
    <ConnectorContainer>
      <StyledOperator>{label}</StyledOperator>
    </ConnectorContainer>
  );
};

export default ({ query }: IProps) => {
  const getPartitionValue = (
    stepIndex: number,
    conversionWindow: Maybe<IDurationFieldsFragment>,
  ): IPartitionValueFieldsFragment | undefined => {
    if (stepIndex === 0) {
      return;
    }

    return conversionWindow
      ? {
          __typename: 'PartitionValue',
          partition: partitionDictionary.ConversionWindow,
          conversionWindow,
        }
      : {
          __typename: 'PartitionValue',
          partition: partitionDictionary.None,
          conversionWindow: null,
        };
  };

  return (
    <BuilderInfoUI.VerticalRow>
      {query.steps.map((step, index) => {
        const negativeSteps = query.negativeSteps;
        const currentRowNegativeSteps = negativeSteps.filter((negativeStep) => {
          return negativeStep.seq === index;
        });

        const partitionValue = getPartitionValue(index, step?.conversionWindow);
        const hasPartition =
          partitionValue &&
          partitionValue.partition.__typename !== 'NonePartition';
        const hasExcludedEvents = currentRowNegativeSteps.length > 0;

        return (
          <Fragment key={`funnel-step-info-${index}`}>
            {index > 0 && (
              <StepOrder
                stepFunnelOrder={query.stepOrders[index - 1]}
                globalFunnelOrder={query.order}
              />
            )}
            {(hasPartition || hasExcludedEvents) && (
              <AddonContainer>
                {hasPartition && (
                  <FunnelStepPartitionContent partitionValue={partitionValue} />
                )}
                {hasExcludedEvents &&
                  currentRowNegativeSteps.map(
                    (negativeStep, negativeStepIndex) => {
                      return (
                        <BuilderInfoUI.VerticalRow
                          key={'negativeStep-' + negativeStepIndex}
                        >
                          {negativeStep.stepConditions &&
                            negativeStep.stepConditions.map(
                              (eventCondition, eventIndex) => (
                                <EventConditionInfo
                                  isNegativeStep={true}
                                  key={
                                    negativeStep.name +
                                    '-' +
                                    index +
                                    '-' +
                                    eventIndex
                                  }
                                  condition={eventCondition}
                                  label={'Event(s)'}
                                  noMarginBottom={true}
                                />
                              ),
                            )}
                        </BuilderInfoUI.VerticalRow>
                      );
                    },
                  )}
              </AddonContainer>
            )}
            <StepContainer>
              <StyledStepLabel size="small">
                {String(index + 1)}
              </StyledStepLabel>
              <EventContainer>
                <BuilderInfoUI.VerticalRow>
                  {step.stepConditions &&
                    step.stepConditions.map((eventCondition, eventIndex) => (
                      <EventConditionInfo
                        key={step.name + '-' + index + '-' + eventIndex}
                        condition={eventCondition}
                        label={'Event(s)'}
                        noMarginBottom={true}
                      />
                    ))}
                </BuilderInfoUI.VerticalRow>
              </EventContainer>
            </StepContainer>
          </Fragment>
        );
      })}
    </BuilderInfoUI.VerticalRow>
  );
};
